import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Absence } from "../../features/absences/models/Absence";
import { Member } from "../../features/members/models/Member";

interface AbsenceDialogProps {
  /** Present when editing; absent when recording a new absence. */
  absence?: Absence;
  members: Member[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (memberId: string, startDate: string, endDate: string, note: string) => void;
}

/** Record or edit a member's absence: member, inclusive date range, optional note. */
const AbsenceDialog: React.FC<AbsenceDialogProps> = ({
  absence,
  members,
  busy,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [memberId, setMemberId] = useState(absence?.memberId ?? members[0]?.id ?? "");
  const [startDate, setStartDate] = useState(absence?.startDate ?? "");
  const [endDate, setEndDate] = useState(absence?.endDate ?? "");
  const [note, setNote] = useState(absence?.note ?? "");

  const valid = memberId !== "" && startDate !== "" && endDate !== "" && startDate <= endDate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl lg:p-6 dark:bg-primary-10">
        <h3 className="text-grey-10 dark:text-grey-90">
          {absence
            ? t("absences.editTitle", "Edit absence")
            : t("absences.recordTitle", "Record absence")}
        </h3>

        <div className="mt-4 space-y-3">
          {!absence && (
            <div>
              <label htmlFor="absence-member" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                {t("absences.memberLabel", "Member")}
              </label>
              <select
                id="absence-member"
                className="input"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                disabled={busy}
              >
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.firstName} {member.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="absence-from" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                {t("absences.fromLabel", "From")}
              </label>
              <input
                id="absence-from"
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="absence-to" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                {t("absences.toLabel", "To")}
              </label>
              <input
                id="absence-to"
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          <div>
            <label htmlFor="absence-note" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
              {t("absences.noteLabel", "Note (optional)")}
            </label>
            <input
              id="absence-note"
              type="text"
              className="input"
              value={note}
              maxLength={500}
              placeholder={t("absences.notePlaceholder", "e.g. summer holidays")}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSubmit(memberId, startDate, endDate, note)}
            disabled={busy || !valid}
          >
            {absence ? t("common.save", "Save") : t("absences.recordAction", "Record absence")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AbsenceDialog;
